import React from "react";

const geist = { fontFamily: "'Geist', sans-serif" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };

const PinIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11Z"
      stroke="#FFFFFF"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" stroke="#FFFFFF" strokeWidth="1.6" />
  </svg>
);

const ChecklistIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="m3.5 7 1.6 1.6L8 5.5" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m3.5 15 1.6 1.6L8 13.5" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11.5 8h9M11.5 16h9" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="8.25" stroke="#FFFFFF" strokeWidth="1.6" />
    <path d="M12 7.5V12l3 1.8" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FEATURES = [
  {
    icon: PinIcon,
    title: "Indigenous understanding",
    body: "We're rooted in Anambra and built for the markets of the East. We know your trade cycles, your seasons, and how your business really moves.",
    wide: false,
  },
  {
    icon: ChecklistIcon,
    title: "No unnecessary bureaucracy",
    body: "Clear requirements, plain language, and a real person to talk to. We ask for what matters and skip the paperwork that doesn't.",
    wide: false,
  },
  {
    icon: ClockIcon,
    title: "Faster approvals",
    body: "No waiting weeks for a decision. We understand that business opportunities don't stay open forever, which is why we keep our process straightforward and efficient. Most SME applications receive a response within 48 hours, giving you the confidence to make decisions, seize opportunities, and keep your business moving forward without unnecessary delays.",
    wide: true,
  },
];

const FeatureCard = ({ feature }) => {
  const Icon = feature.icon;
  return (
    <div
      style={{
        gridColumn: feature.wide ? "1 / -1" : "auto",
        backgroundColor: "#FFFFFF",
        border: "1px solid #ECEAE2",
        borderRadius: "16px",
        padding: "clamp(24px, 2.2vw, 32px)",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "12px",
          backgroundColor: "#132A22",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon />
      </div>

      <h3
        style={{
          ...geist,
          fontWeight: 600,
          fontSize: "clamp(17px, 1.39vw, 20px)",
          lineHeight: 1.3,
          color: "#132A22",
          margin: 0,
        }}
      >
        {feature.title}
      </h3>

      <p
        style={{
          ...geist,
          fontWeight: 400,
          fontSize: "clamp(13px, 1.04vw, 15px)",
          lineHeight: 1.7,
          color: "#5A6B60",
          margin: 0,
          maxWidth: feature.wide ? "880px" : "none",
        }}
      >
        {feature.body}
      </p>
    </div>
  );
};

const WhyZikora = () => {
  return (
    <>
      <style>{`
        .sme-why-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(16px, 1.67vw, 24px);
          margin-top: clamp(40px, 4.5vw, 64px);
        }
        @media (max-width: 767px) {
          .sme-why-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <section
        style={{
          width: "100%",
          backgroundColor: "transparent",
          boxSizing: "border-box",
          padding:
            "clamp(56px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#66A681",
                  flexShrink: 0,
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  ...geistMono,
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: 1,
                  letterSpacing: "0.1em",
                  color: "#295B4A",
                  textTransform: "uppercase",
                }}
              >
                Why Zikora for SMEs
              </span>
            </div>

            <h2
              style={{
                ...instrumentSerif,
                fontWeight: 400,
                fontSize: "clamp(30px, 3.61vw, 52px)",
                lineHeight: 1.08,
                letterSpacing: "-0.01em",
                color: "#132A22",
                margin: 0,
                maxWidth: "640px",
              }}
            >
              Built with local businesses in mind
            </h2>

            <p
              style={{
                ...geist,
                fontWeight: 400,
                fontSize: "clamp(14px, 1.25vw, 18px)",
                lineHeight: 1.65,
                color: "#5A6B60",
                margin: 0,
                maxWidth: "600px",
              }}
            >
              We understand how businesses in Southeast Nigeria operate, and
              we've designed our support around that reality.
            </p>
          </div>

          <div className="sme-why-grid">
            {FEATURES.map((feature) => (
              <FeatureCard key={feature.title} feature={feature} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default WhyZikora;
